import { computed, inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import type { NonFiscalDocuments, Printers } from 'tilby-models';

import {
    ConfigurationManagerService,
    ConfigurationPreferences,
    EntityManagerService,
    ErrorsLoggerService,
    LoaderService,
    UserActiveSessionManagerService
} from 'src/app/core';

import { AlertDialogService, DocumentPrintersManagerDialogStateService } from 'src/app/dialogs';

import type {
    PreferenceSettingDefinition,
    SettingDefinition,
    SettingError,
    SettingsContext,
    SettingsDataSources,
    SettingsDocumentPrinter,
    SettingsValues,
    SettingValue
} from '../models/settings-hub.model';

import { SETTINGS_REGISTRY } from '../registry/settings-registry';

import {
    areSettingValuesEquivalent,
    coerceSettingValue,
    emptySettingValue,
    flattenSettings,
    getSettingError,
    isPreferenceSetting,
    isSettingDisabled,
    isSettingReadonly,
    isSettingVisible,
    normalizeSettingValue,
    serializeSettingValue
} from '../utils/settings-hub.utils';

type PreferenceKey = keyof ConfigurationPreferences;
type DocumentPrinter = Parameters<DocumentPrintersManagerDialogStateService['scanPrintersCapabilities']>[0][number];

/**
 * Holds the values of all the settings of the registry while the user edits them.
 *
 * - `initialValues` mirrors what is saved; `values` is what the user sees. Their difference gives the changed keys.
 * - Every rule of the registry (visibleWhen, disabledWhen, required...) is evaluated against `context()`.
 * - Nothing is written to the preferences until `save()`.
 * Provided by the hub component, so each page instance has its own state.
 */
@Injectable()
export class SettingsHubStateService {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly documentPrintersManager = inject(DocumentPrintersManagerDialogStateService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly errorsLoggerService = inject(ErrorsLoggerService);
    private readonly loaderService = inject(LoaderService);
    private readonly snackBar = inject(MatSnackBar);
    private readonly translateService = inject(TranslateService);
    private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);

    private readonly preferenceSettings = flattenSettings(SETTINGS_REGISTRY).map(({ setting }) => setting).filter(isPreferenceSetting);
    /** A preference can be shown by more than one setting (e.g. a different control for each mode) */
    private readonly settingsByKey = this.groupByPreferenceKey(this.preferenceSettings);
    private readonly nonBlockingSettingIds = new Set(this.preferenceSettings.filter(setting => setting.nonBlocking).map(setting => setting.id));
    private readonly initialValues = signal<SettingsValues>({});
    private saving = false;
    /** The changed preferences that the last save wrote, even if it failed later: what has to follow a save depends on them */
    private savedKeys: PreferenceKey[] = [];
    private readonly data = signal<SettingsDataSources>({ departments: [], nonFiscalDocuments: [], paymentMethods: [], printers: [], documentPrinters: [] });
    private readonly isDemoShop = !!this.userActiveSessionManagerService.getSession()?.shop?.name?.startsWith('demo_');

    /** Tells whether the payment methods being edited have changes that are not saved: they are saved on their own, so the page does not know */
    private paymentMethodChangesCheck: (() => boolean) | null = null;

    /** True once the collections needed by the settings have been loaded */
    readonly ready = signal(false);

    readonly values = signal<SettingsValues>({});

    readonly context = computed<SettingsContext>(() => {
        const values = this.values();

        return {
            values,
            data: this.data(),
            isDemoShop: this.isDemoShop,
            isModuleEnabled: module => this.configurationManagerService.isModuleEnabled(module),
            // A value that the user cleared is null, not missing: only the preferences that the page does not show come from the saved ones
            getPreference: key => (key in values ? values[key] : this.configurationManagerService.getPreference(key) ?? null) as SettingValue,
            getSavedPreference: key => (this.configurationManagerService.getPreference(key) ?? null) as SettingValue
        };
    });

    readonly changedKeys = computed(() => {
        const context = this.context();
        const initialValues = this.initialValues();

        return [...this.settingsByKey.entries()]
            .filter(([key, settings]) => !settings.every(setting => isSettingReadonly(setting, context)) && !areSettingValuesEquivalent(settings[0], context.values[key], initialValues[key]))
            .map(([key]) => key);
    });

    readonly isDirty = computed(() => this.changedKeys().length > 0);

    private readonly errors = computed(() => {
        const context = this.context();
        const errors: Record<string, SettingError> = {};

        for (const setting of this.preferenceSettings) {
            const error = isSettingVisible(setting, context) ? getSettingError(setting, context.values[setting.preferenceKey] ?? null, context) : null;

            if (error) {
                errors[setting.id] = error;
            }
        }

        return errors;
    });

    /**
     * Whether there is an error that stops the save. The legacy page only stops for the required fields that are empty and ignores
     * the values that are not valid, so here an error stops the save only if it is on a setting that the user changed or it is a
     * missing required value: a value saved earlier that is not valid does not stop the user from saving something else.
     * The errors of the non blocking settings are only shown.
     */
    readonly hasErrors = computed(() => {
        const changedKeys = new Set<string>(this.changedKeys());

        return this.preferenceSettings.some(setting => {
            const error = this.errors()[setting.id];

            return !!error && !this.nonBlockingSettingIds.has(setting.id) && (error === 'required' || changedKeys.has(setting.preferenceKey));
        });
    });

    constructor() {
        this.load();
    }

    /**
     * Loads the collections that some settings need for their options.
     * A collection that cannot be read is left empty: the page stays usable, and only the settings that need it have no options.
     */
    async init() {
        const entityManager = this.entityManagerService;
        const orEmpty = <T>(collection: Promise<T[]>) => collection.catch((): T[] => []);

        try {
            const [departments, nonFiscalDocuments, paymentMethods, printers] = await Promise.all([
                orEmpty(entityManager.departments.fetchCollectionOffline()),
                orEmpty(entityManager.nonfiscalDocuments.fetchCollectionOffline()),
                orEmpty(entityManager.paymentMethods.fetchCollectionOffline()),
                orEmpty(entityManager.printers.fetchCollectionOffline())
            ]);

            this.data.set({ departments, nonFiscalDocuments, paymentMethods, printers, documentPrinters: this.getDocumentPrinters(printers, nonFiscalDocuments) });
        } catch (error) {
            // Whatever goes wrong, the page is shown: the settings that need the collections just have no options
            this.errorsLoggerService.err(error);
        } finally {
            this.ready.set(true);
        }
    }

    /**
     * The printers that print documents, each with the documents that it can print: the same ones that the dialog of the
     * automatic print offers (the e-invoice and its summaries are not for the sales that are printed automatically).
     */
    private getDocumentPrinters(printers: Printers[], nonFiscalDocuments: NonFiscalDocuments[]): SettingsDocumentPrinter[] {
        try {
            const documentPrinters: DocumentPrinter[] = structuredClone(printers.filter(printer => ['fiscal', 'rt', 'receipt'].includes(printer.type)));

            this.documentPrintersManager.scanPrintersCapabilities(documentPrinters, nonFiscalDocuments, {});

            return documentPrinters.map(printer => ({
                id: printer.id!,
                name: printer.name,
                documents: (printer.availableDocuments ?? [])
                    .filter(document => !['e_invoice', 'summary_e_rc', 'summary_e_nrc'].includes(document.document_type.id))
                    .map(document => ({
                        value: String(document.document_template?.id || document.document_type.id),
                        label: this.documentPrintersManager.getDocumentTypeName(document, 'CASHREGISTER.DOCUMENTS_MANAGER') || ''
                    }))
            }));
        } catch {
            // The settings that need them just have no options
            return [];
        }
    }

    /** Reloads the payment methods, that some settings need to know (e.g. the ones of the PMS) */
    async reloadPaymentMethods() {
        const paymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline();

        this.data.update(data => ({ ...data, paymentMethods }));
    }

    /** The editor of the payment methods tells how to know if it has changes not saved; the function returned stops telling it */
    registerPaymentMethodChangesCheck(check: () => boolean) {
        this.paymentMethodChangesCheck = check;

        return () => {
            if (this.paymentMethodChangesCheck === check) {
                this.paymentMethodChangesCheck = null;
            }
        };
    }

    hasPaymentMethodChanges() {
        return !!this.paymentMethodChangesCheck?.();
    }

    /** The changed preferences written by the last save, once: the next call gives none until another save writes something */
    takeSavedKeys(): PreferenceKey[] {
        const savedKeys = this.savedKeys;
        this.savedKeys = [];

        return savedKeys;
    }

    /** A save is running: asking for another one does nothing */
    get isSaving() {
        return this.saving;
    }

    getValue(setting: SettingDefinition): SettingValue {
        return isPreferenceSetting(setting) ? this.values()[setting.preferenceKey] ?? null : null;
    }

    getError(setting: SettingDefinition): SettingError | null {
        return this.errors()[setting.id] ?? null;
    }

    isDisabled(setting: SettingDefinition) {
        return isSettingDisabled(setting, this.context());
    }

    /** Sets a value, resets the settings that depend on it and applies the side effects declared by the setting (`onChange`) */
    setValue(setting: PreferenceSettingDefinition, value: SettingValue) {
        const updatedValues: SettingsValues = { ...this.values(), [setting.preferenceKey]: coerceSettingValue(setting, value) };

        for (const key of setting.resetOnChange ?? []) {
            const dependentSetting = this.settingsByKey.get(key)?.[0];

            if (dependentSetting) {
                updatedValues[key] = emptySettingValue(dependentSetting);
            }
        }

        this.values.set(updatedValues);

        if (setting.onChange) {
            this.applyValues(setting.onChange(value, this.context()));
        }
    }

    /** Applies the values returned by an action; the preferences not shown in the page are ignored */
    applyValues(values: SettingsValues, persisted = false) {
        const updatedValues: SettingsValues = { ...this.values() };
        const updatedInitialValues: SettingsValues = { ...this.initialValues() };

        for (const [key, value] of Object.entries(values) as [PreferenceKey, SettingValue | undefined][]) {
            const setting = this.settingsByKey.get(key)?.[0];

            if (setting) {
                updatedValues[key] = value ?? emptySettingValue(setting);

                if (persisted) {
                    updatedInitialValues[key] = updatedValues[key];
                }
            }
        }

        this.values.set(updatedValues);

        if (persisted) {
            this.initialValues.set(updatedInitialValues);
        }
    }

    /** Drops every change that is not saved */
    discard() {
        this.values.set({ ...this.initialValues() });
    }

    /**
     * Writes the changed preferences. Returns false when nothing was saved because of validation errors or a failure.
     * Empty values of `deleteOnEmpty` settings remove the preference; `onlineFirst` ones try the server before the local copy.
     */
    async save(): Promise<boolean> {
        // A second click while the first save is running would write everything twice
        if (this.saving) {
            return false;
        }

        // Some settings drop the edits of others before the page is saved (e.g. the fields of an integration that is off)
        for (const setting of this.preferenceSettings) {
            if (setting.beforeSave) {
                this.applyValues(setting.beforeSave(this.values()[setting.preferenceKey] ?? null, this.context()));
            }
        }

        const changedKeys = this.changedKeys();

        if (!changedKeys.length) {
            return true;
        }

        if (this.hasErrors()) {
            return false;
        }

        // The defaults that were never saved are saved along with the changes, as the legacy page does
        const pendingDefaultKeys = this.preferenceSettings
            .filter(setting => setting.persistDefault && this.configurationManagerService.getShopPreference(setting.preferenceKey) == null && !changedKeys.includes(setting.preferenceKey))
            .map(setting => setting.preferenceKey);

        const values = this.values();
        this.saving = true;
        this.savedKeys = [];
        this.loaderService.show();

        try {
            for (const key of [...changedKeys, ...pendingDefaultKeys]) {
                const setting = this.settingsByKey.get(key)![0];
                const value = serializeSettingValue(setting, values[key] ?? null);

                if (setting.deleteOnEmpty && (value === '' || value === false)) {
                    this.configurationManagerService.deleteShopPreference(key);
                } else {
                    await this.setShopPreference(setting, value as ConfigurationPreferences[typeof key]);
                }

                // What is saved is no longer a change: if a later key fails, a new attempt only saves the ones left
                this.initialValues.update(initialValues => ({ ...initialValues, [key]: values[key] }));

                if (changedKeys.includes(key)) {
                    this.savedKeys.push(key);
                }
            }

            this.initialValues.set({ ...values });

            this.snackBar.open(this.translateService.instant('SETTINGS.SETTINGS.SAVE_EDIT_SUCCESSFUL'), undefined, {
                duration: 3000,
                horizontalPosition: 'start',
                verticalPosition: 'bottom'
            });

            return true;
        } catch (error) {
            this.errorsLoggerService.err(error);
            this.alertDialogService.openDialog({
                data: {
                    messageLabel: this.translateService.instant('SETTINGS.SETTINGS.SAVE_EDIT_ERROR')
                }
            });

            return false;
        } finally {
            this.saving = false;
            this.loaderService.hide();
        }
    }

    private async setShopPreference<K extends PreferenceKey>(setting: PreferenceSettingDefinition, value: ConfigurationPreferences[K]) {
        const key = setting.preferenceKey as K;

        if (!setting.onlineFirst) {
            return this.configurationManagerService.setShopPreference(key, value);
        }

        try {
            await this.configurationManagerService.setShopPreferenceSync(key, value);
        } catch (error) {
            this.configurationManagerService.setShopPreference(key, value);
        }
    }

    private load() {
        const values: SettingsValues = {};

        for (const [key, settings] of this.settingsByKey) {
            values[key] = normalizeSettingValue(settings[0], this.configurationManagerService.getShopPreference(key));
        }

        this.initialValues.set({ ...values });
        this.values.set(values);
    }

    private groupByPreferenceKey(settings: PreferenceSettingDefinition[]) {
        const settingsByKey = new Map<PreferenceKey, PreferenceSettingDefinition[]>();

        for (const setting of settings) {
            settingsByKey.set(setting.preferenceKey, [...(settingsByKey.get(setting.preferenceKey) ?? []), setting]);
        }

        return settingsByKey;
    }
}
